import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, BookPlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, useTicketTitle } from "@/lib/hooks";
import { StatusBadge, AuthImage } from "@/components/common";
import { PhotoUpload } from "@/components/PhotoUpload";
import { AiCard } from "@/components/TicketDialog";
import { StaffControls } from "@/components/StaffControls";
import { Field } from "@/components/PropertyDialog";
import { SlaBadge } from "@/components/FormBits";
import { DateTimeInput } from "@/components/DateInput";
import { TicketQuotes, ConciergePanel } from "@/components/TicketExtras";

const STAFF = ["admin", "supervision", "gestionnaire"];

function actionsFor(role, status) {
  if (STAFF.includes(role)) return ["ouvert", "attribue", "en_cours", "resolu", "cloture"].filter((s) => s !== status);
  if (role === "prestataire") return { attribue: ["en_cours", "resolu"], en_cours: ["resolu"] }[status] || [];
  if (status === "resolu") return ["cloture", "ouvert"];
  return status === "cloture" ? ["ouvert"] : [];
}

function Attachments({ items }) {
  if (!items?.length) return null;
  return <div className="mt-2 flex flex-wrap gap-2">{items.map((p) => p.endsWith(".pdf") ? <span key={p} className="rounded-sm border px-2 py-1 text-xs">PDF</span> : <AuthImage key={p} path={p} className="h-20 w-24 rounded-sm object-cover" />)}</div>;
}

function Event({ ev }) {
  const { t, fdatetime } = usePrefs();
  const sys = ["status", "assign", "triage", "quote", "invoice", "escalation", "schedule"].includes(ev.type);
  return (
    <li className="relative pl-6" data-testid={`event-${ev.id}`}>
      <span className={`absolute left-[-5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white ${sys ? "bg-[#B8860B]" : "bg-[#0F172A]"}`} />
      <div className="text-xs text-slate-500"><span className="font-semibold text-slate-800">{ev.user_name}</span> · {t(`role_${ev.role}`)} · {fdatetime(ev.created_at)}</div>
      {ev.type === "status" && <div className="mt-1 flex items-center gap-2 text-sm"><StatusBadge value={ev.meta.from} /> → <StatusBadge value={ev.meta.to} /></div>}
      {ev.text && <div className={`mt-1 text-sm ${sys ? "italic text-slate-600" : "rounded-sm bg-slate-50 p-3 text-slate-800"}`}>{ev.text}</div>}
      <Attachments items={ev.attachments} />
    </li>
  );
}

function ScheduleBox({ tk, canEdit }) {
  const { t, fdatetime } = usePrefs();
  const qc = useQueryClient();
  const local = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16); };
  const [v, setV] = useState(local(tk.planned_at));
  const save = async () => {
    try { await api.put(`/tickets/${tk.id}/schedule`, { planned_at: new Date(v).toISOString() }); qc.invalidateQueries({ queryKey: ["ticket", tk.id] }); toast.success(t("plannedAt")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!canEdit && !tk.planned_at) return null;
  return (
    <div className="border-t border-slate-100 pt-3" data-testid="schedule-box">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t("plannedAt")}</div>
      {!canEdit ? <div data-testid="planned-at">{fdatetime(tk.planned_at)}</div> : (
        <div className="space-y-2">
          <DateTimeInput value={v} onChange={(e) => setV(e.target.value)} data-testid="schedule-input" />
          <Button size="sm" variant="outline" className="w-full rounded-sm" disabled={!v} onClick={save} data-testid="schedule-save-button">{t("planIntervention")}</Button>
        </div>
      )}
    </div>
  );
}

function FaqDialog({ tk }) {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: tk.title, problem: tk.description, solution: tk.ai_suggestion?.solution || "" });
  const save = async () => {
    try { await api.post("/faq", { ...f, category: tk.category, source_ticket_id: tk.id }); toast.success(t("toFaq")); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="w-full rounded-sm" data-testid="to-faq-button"><BookPlus size={14} className="mr-1" />{t("toFaq")}</Button></DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="faq-from-ticket-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("toFaq")}</DialogTitle></DialogHeader>
        <Field label={t("title")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="faq-title-input" /></Field>
        <Field label={t("problem")}><Textarea value={f.problem} onChange={(e) => setF({ ...f, problem: e.target.value })} /></Field>
        <Field label={t("solution")}><Textarea rows={4} value={f.solution} onChange={(e) => setF({ ...f, solution: e.target.value })} data-testid="faq-solution-input" /></Field>
        <Button onClick={save} disabled={!f.solution} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="faq-save-button">{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const title = useTicketTitle();
  const { data: tk } = useQuery({ queryKey: ["ticket", id], queryFn: () => fetcher(`/tickets/${id}`), refetchInterval: 8000 });
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  if (!tk) return <div className="h-64 animate-pulse bg-slate-200/60" />;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ticket", id] }); qc.invalidateQueries({ queryKey: ["tickets"] }); };
  const send = async () => {
    try { await api.post(`/tickets/${id}/comments`, { text, attachments: files }); setText(""); setFiles([]); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (s) => {
    try { await api.post(`/tickets/${id}/status`, { status: s }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const staff = STAFF.includes(user.role);
  return (
    <div data-testid="ticket-detail-page">
      <Link to={tk.kind === "conciergerie" ? "/concierge" : "/tickets"} className="mb-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"><ArrowLeft size={13} />{t("tickets")}</Link>
      <div className="rise mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow font-data">{tk.number}</div>
          <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl" data-testid="ticket-title">{title(tk)}</h1>
          <div className="mt-2 text-sm text-slate-500">{tk.property?.name}{tk.unit_ref ? ` · ${tk.unit_ref}` : ""} · {tk.property?.city}</div>
        </div>
        <div className="flex items-center gap-2"><SlaBadge tk={tk} /><StatusBadge value={tk.urgency} testId="ticket-urgency-badge" /><StatusBadge value={tk.status} testId="ticket-status-badge" /></div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <section className="rounded-sm border border-slate-200 bg-white p-6">
          <h3 className="mb-5 font-display text-lg font-bold">{t("history")}</h3>
          <ol className="space-y-6 border-l border-slate-200" data-testid="ticket-thread">{tk.events.map((ev) => <Event key={ev.id} ev={ev} />)}</ol>
          <div className="mt-8 border-t border-slate-100 pt-5">
            <Textarea placeholder={t("addComment")} value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input" />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <PhotoUpload value={files} onChange={setFiles} testId="comment-photo-upload" />
              <Button onClick={send} disabled={!text.trim() && !files.length} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="comment-send-button"><Send size={14} className="mr-1" />{t("comment")}</Button>
            </div>
          </div>
        </section>
        <aside className="space-y-5">
          <div className="space-y-3 rounded-sm border border-slate-200 bg-white p-5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("category")}</span><span data-testid="ticket-category">{label(tk.category)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("assignedTo")}</span><span data-testid="ticket-assigned">{tk.assigned_name || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("createdBy")}</span><span>{tk.created_by_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("date")}</span><span>{fdatetime(tk.created_at)}</span></div>
            {!["resolu", "cloture"].includes(tk.status) && <ScheduleBox key={tk.planned_at || "x"} tk={tk} canEdit={staff || (user.role === "prestataire" && tk.assigned_to === user.id)} />}
            {actionsFor(user.role, tk.status).length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                {actionsFor(user.role, tk.status).map((s) => (
                  <button key={s} onClick={() => setStatus(s)} data-testid={`status-action-${s}`} className="rounded-sm border border-slate-300 px-2.5 py-1 text-xs font-semibold transition-colors hover:border-[#0F172A] hover:bg-[#0F172A] hover:text-white">
                    {s === "ouvert" && ["resolu", "cloture"].includes(tk.status) ? t("reopen") : s === "cloture" ? t("close") : `→ ${t(s)}`}
                  </button>
                ))}
              </div>
            )}
          </div>
          {tk.kind === "conciergerie" && <ConciergePanel tk={tk} />}
          <TicketQuotes tk={tk} />
          {tk.ai_suggestion && <AiCard s={tk.ai_suggestion} />}
          {staff && <div className="rounded-sm border border-slate-200 bg-white p-5"><StaffControls tk={tk} /></div>}
          {staff && ["resolu", "cloture"].includes(tk.status) && !tk.faq_id && <FaqDialog tk={tk} />}
        </aside>
      </div>
    </div>
  );
}
